/**
 * Die zweite Steuerung: laufen mit der Maus (Produktentscheidung vom
 * 25.10.2026).
 *
 * Robin wollte am Computer die Bedienung, die man von dort kennt: laufen mit
 * dem Rechtsklick, Faehigkeiten auf Q/W/E/R, ausgeloest mit dem Linksklick.
 * Umschaltbar in den Einstellungen; der Standard bleibt, was es vorher gab.
 *
 * WARUM DER SERVER DAVON NICHTS WEISS: Er bekommt pro Takt eine RICHTUNG und
 * nie eine Position (Masterprompt 28). Woher die Richtung kommt - Daumen,
 * WASD oder ein angeklickter Punkt -, ist seine Sache nicht. Deshalb ist
 * diese Steuerung reine Clientarbeit: kein neues Feld im Zustand, keine
 * Protokollaenderung, keine Wirkung auf den Massentest.
 *
 * WAS SICH ZWANGSLAEUFIG MITAENDERT: Mit Q/W/E/R liegt Skill 2 auf dem W -
 * also derselben Taste, die vorher nach vorne lief. In dieser Steuerung ist
 * WASD deshalb aus, und gelaufen wird nur noch mit der Maus. Das ist keine
 * Wahl, sondern die Folge der Belegung.
 */

import type { Vec2 } from '@dueline/shared';
import type { MoveVector } from './InputController.js';

/** Die beiden Bedienarten. */
export type Steuerung = 'tasten' | 'maus';

/**
 * Tastenbelegung der vier Skills, je Steuerung.
 *
 * Die EINE Stelle, an der sie steht: Die Tastatur fragt sie, und die
 * Beschriftung der Schaltflaechen leitet sich daraus ab. Zwei Listen waeren
 * ein Versprechen, das irgendwann bricht.
 */
export const SKILL_TASTEN: Readonly<Record<Steuerung, Readonly<Record<string, number>>>> = {
  tasten: { Digit1: 1, Digit2: 2, Digit3: 3, KeyR: 4 },
  maus: { KeyQ: 1, KeyW: 2, KeyE: 3, KeyR: 4 },
};

/** Beschriftung der Schaltflaechen - abgeleitet aus der Belegung. */
export function skillBeschriftung(steuerung: Steuerung, slot: number): string {
  for (const [code, belegt] of Object.entries(SKILL_TASTEN[steuerung])) {
    if (belegt !== slot) continue;
    // "Digit1" -> "1", "KeyQ" -> "Q"
    if (code.startsWith('Digit')) return code.slice(5);
    if (code.startsWith('Key')) return code.slice(3);
    return code;
  }
  return String(slot);
}

/**
 * Wie nah der Hero an seinen Zielpunkt herankommen muss, um anzuhalten.
 *
 * Zu klein, und er zappelt um den Punkt herum, weil jeder Takt ihn ein
 * Stueck darueber hinaustraegt. Zu gross, und er bleibt sichtbar davor
 * stehen. TUNING_PLACEHOLDER.
 */
export const KLICK_HALT_ABSTAND = 0.6;

/**
 * Wie viel von der eigenen Angriffsreichweite als Zielabstand gilt, wenn man
 * einen Gegner anklickt.
 *
 * Nicht die ganze Reichweite: Client und Server stehen nie auf genau derselben
 * Position (der Client sagt die eigene Bewegung voraus). Wer bis an die
 * aeusserste Kante heranlaeuft, steht aus Sicht des Servers vielleicht einen
 * Fingerbreit zu weit weg - und schlaegt dann nicht zu. TUNING_PLACEHOLDER.
 */
export const ANGRIFF_HALT_ANTEIL = 0.85;

/**
 * Die Laufrichtung zu einem Zielpunkt - oder Stillstand, wenn er da ist.
 *
 * Alles oder nichts, wie beim Joystick (Produktentscheidung vom 23.10.2026):
 * Die Richtung hat immer die Laenge 1. Ein halber Schritt waere eine zweite
 * Art, Tempo zu bestimmen - und genau die ist abgeschafft worden.
 */
export function klickRichtung(von: Vec2, ziel: Vec2, haltAbstand: number): MoveVector {
  const dx = ziel.x - von.x;
  const dy = ziel.y - von.y;
  const abstand = Math.hypot(dx, dy);

  // Ungueltige Zahlen gelten als "kein Ziel" - sonst wandert der Hero ins Nichts.
  if (!Number.isFinite(abstand)) return { x: 0, y: 0 };
  if (abstand <= Math.max(0, haltAbstand)) return { x: 0, y: 0 };

  return { x: dx / abstand, y: dy / abstand };
}

/**
 * Platz im Browserspeicher fuer die gewaehlte Steuerung.
 *
 * Dauerhaft und nicht nur fuer die Sitzung: Wer am Computer spielt, will
 * seine Bedienung nicht bei jedem Oeffnen neu einstellen.
 */
const SPEICHER_SCHLUESSEL = 'dueline.steuerung';

/**
 * Der Speicher des Browsers, falls es einen gibt.
 *
 * Hineingegeben statt genommen - dasselbe Muster wie bei den
 * Wellen-Speicherplaetzen: Im Testlauf gibt es kein localStorage, und diese
 * Regel soll trotzdem geprueft sein.
 */
function standardSpeicher(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    // Privates Fenster oder gesperrte Daten.
    return null;
  }
}

/** Die gemerkte Steuerung. Standard ist die bisherige. */
export function ladeSteuerung(quelle: Storage | null = standardSpeicher()): Steuerung {
  try {
    return quelle?.getItem(SPEICHER_SCHLUESSEL) === 'maus' ? 'maus' : 'tasten';
  } catch {
    // Gesperrter Speicher: dann eben der Standard.
    return 'tasten';
  }
}

/** Merkt sich die Steuerung fuer das naechste Mal. */
export function speichereSteuerung(
  steuerung: Steuerung,
  quelle: Storage | null = standardSpeicher(),
): void {
  try {
    quelle?.setItem(SPEICHER_SCHLUESSEL, steuerung);
  } catch {
    // Dann gilt sie eben nur fuer diese Sitzung.
  }
}

/** Wohin der Hero auf Befehl laeuft. */
export type Laufziel = { art: 'punkt'; punkt: Vec2 } | { art: 'einheit'; id: string };

/**
 * Der laufende Klickbefehl (Produktentscheidung vom 25.10.2026).
 *
 * Eine Klasse und kein React-Zustand, aus zwei Gruenden:
 *
 *   - Die Richtung wird in JEDEM Takt abgefragt (20 mal je Sekunde). Ein
 *     Zustand wuerde dabei neu zeichnen lassen, ohne dass sich im Bild
 *     etwas aendert.
 *   - Dasselbe Muster wie bei SkillInput: Wer veraenderlichen Zustand
 *     ausserhalb von React haelt, haelt ihn in einem Objekt - nicht in
 *     einem Ref, in das von ueberall geschrieben wird.
 */
export class MausBefehl {
  private ziel: Laufziel | null = null;

  /** Der laufende Befehl, oder null. */
  gib(): Laufziel | null {
    return this.ziel;
  }

  /** Dorthin laufen und dort bleiben. */
  setzePunkt(punkt: Vec2): void {
    this.ziel = { art: 'punkt', punkt: { x: punkt.x, y: punkt.y } };
  }

  /** Dieser Einheit nachlaufen, bis sie in Reichweite ist. */
  setzeEinheit(id: string): void {
    this.ziel = { art: 'einheit', id };
  }

  /**
   * Die Laufrichtung zu diesem Befehl - oder Stillstand.
   *
   * Gerechnet wird mit der Position, die der SPIELER SIEHT (der Client sagt
   * die eigene Bewegung voraus). Mit der reinen Serverposition kaeme der Hero
   * sichtbar ueber sein Ziel hinaus, weil die Antwort immer einen Augenblick
   * alt ist.
   *
   * Zwei Arten von Ziel, und sie verhalten sich verschieden:
   *
   *   - ein PUNKT: hin, und dort ist der Befehl ERLEDIGT. Er wird vergessen,
   *     sonst liefe der Hero immer wieder dorthin zurueck, sobald ihn etwas
   *     wegschiebt.
   *   - eine EINHEIT: nachlaufen, bis sie in Angriffsreichweite ist. Der
   *     Befehl BLEIBT, solange sie lebt - sie laeuft ja weiter. Zuschlagen
   *     muss niemand befehlen: Sie ist das gewaehlte Ziel, und im Stehen
   *     schlaegt der Hero von selbst zu (Regel vom 25.10.2026).
   *
   * Stirbt das Ziel oder verschwindet es, verfaellt der Befehl.
   */
  richtung(lage: {
    /** Wo der Hero im Bild steht. */
    eigene: Vec2;
    /** Findet eine Einheit - oder nichts, wenn sie nicht mehr da ist. */
    sucheEinheit: (id: string) => { x: number; y: number; hp: number } | undefined;
    /** Die eigene Angriffsreichweite. */
    reichweite: number;
  }): MoveVector {
    const ziel = this.ziel;
    if (!ziel) return { x: 0, y: 0 };

    if (ziel.art === 'punkt') {
      const richtung = klickRichtung(lage.eigene, ziel.punkt, KLICK_HALT_ABSTAND);
      if (richtung.x === 0 && richtung.y === 0) this.loesche();
      return richtung;
    }

    const gegner = lage.sucheEinheit(ziel.id);
    if (!gegner || gegner.hp <= 0) {
      this.loesche();
      return { x: 0, y: 0 };
    }

    return klickRichtung(
      lage.eigene,
      { x: gegner.x, y: gegner.y },
      lage.reichweite * ANGRIFF_HALT_ANTEIL,
    );
  }

  /**
   * Befehl aufheben.
   *
   * Passiert oefter, als man denkt: beim Umschalten der Steuerung, beim
   * Ankommen, wenn das Ziel stirbt, wenn der Hero stirbt, in der Enemy
   * View und sobald jemand eine Taste oder den Joystick benutzt.
   */
  loesche(): void {
    this.ziel = null;
  }
}
